import type { ArticleImage, ArticleVideo } from '../../types/Article';
import type { CarouselItemProps, CarouselItemWithVideo } from '../../types/carousel-item';
import { getYouTubeImageUrl, YouTubeThumbnailQuality } from '../../utils/youtube';
import { VIDEO_START_INDEX } from './constants';

interface MapResourcesToMediaOptions {
  images: ArticleImage[];
  videos: ArticleVideo[];
  widths: number[];
  alt: string;
  videoQuality?: YouTubeThumbnailQuality;
}

type GetVideoResourceParams = Pick<MapResourcesToMediaOptions, 'widths' | 'alt'> & {
  video: ArticleVideo;
  quality: MapResourcesToMediaOptions['videoQuality'];
};

const getValidVideos = (videos: ArticleVideo[]) => videos.filter(video => video.id);

const getVideoResource = ({
  video: { id },
  quality,
  widths,
  alt,
}: GetVideoResourceParams): CarouselItemWithVideo => ({
  icon: 'play',
  ytId: id,
  image: { src: getYouTubeImageUrl(id, quality), widths, alt },
});

export const mapResourcesToMedia = ({
  images,
  videos,
  widths,
  alt,
  videoQuality,
}: MapResourcesToMediaOptions): CarouselItemProps[] => {
  const mediaResources = images.map<CarouselItemProps>(({ path }) => ({
    image: {
      src: path,
      widths,
      alt,
    },
  }));

  const validVideos = getValidVideos(videos);
  validVideos.forEach((video, index) => {
    mediaResources.splice(
      VIDEO_START_INDEX + index,
      0,
      getVideoResource({ video, widths, alt, quality: videoQuality })
    );
  });

  return mediaResources;
};

export const mapResourcesToMediaItems = ({
  images,
  videos,
  alt,
  videoQuality = YouTubeThumbnailQuality.Default,
}: Omit<MapResourcesToMediaOptions, 'widths'>) => {
  const mediaResources = images.map(({ path }) => ({
    path,
    alt,
    ytId: null,
  }));
  const validVideos = getValidVideos(videos);
  validVideos.forEach((video, index) => {
    mediaResources.splice(VIDEO_START_INDEX + index, 0, {
      path: getYouTubeImageUrl(video.id, videoQuality),
      alt,
      ytId: video.id,
    });
  });

  return mediaResources;
};
